import React, { useState, useEffect, useRef, memo } from 'react';
import { useNetwork } from '@/providers/NetworkProvider';
import { ethers } from 'ethers';
import { NodeStats } from '@/types';
import { NodeTreeMap } from './NodeTreeMap';
import { InventoryPanel } from './InventoryPanel';
import { ShopPanel } from './ShopPanel';
import { AchievementsPanel } from './AchievementsPanel';
import { LeaderboardPanel } from './LeaderboardPanel';
import { ReferralPanel } from './ReferralPanel';
import { SettingsPanel } from './SettingsPanel';
import { HelpPanel } from './HelpPanel';
import { DailyRewardsPanel } from './DailyRewardsPanel';
import { AnalyticsPanel } from './AnalyticsPanel';
import { EffectsCanvas } from './EffectsCanvas';
import { EffectsDisplay } from './EffectsDisplay';
import { TransactionStatus } from './TransactionStatus';
import { AdminPanel } from './AdminPanel';
import { BoostEventsPanel } from './BoostEventsPanel';
import { notify } from '@/utils/notify';
import { useTranslation } from 'react-i18next';
import { 
  formatToken,
  formatSmallAmount,
  formatHashPower,
  formatBandwidth,
  formatUptime,
  getTierName,
  getTierColor
} from '@/utils/format';
import { estimatePendingWei } from '@/utils/rewardMath';
import '@/styles/hacker-dashboard.css';
import '@/styles/inventory.css';
import '@/styles/achievements.css';
import '@/styles/leaderboard.css';
import '@/styles/shop.css';
import '@/styles/boost-events.css';
import '@/styles/node-upgrade.css';
import '@/styles/analytics.css';
import '@/styles/settings.css';
import '@/styles/effects.css';

const CLAIM_CHUNK_SIZE = 25;

const nodesFingerprint = (list: NodeStats[]) =>
  list
    .map(
      (n) =>
        `${n.tokenId}:${n.isActive}:${n.hashPower}:${n.bandwidth}:${n.uptime}:${n.lastClaimTime}:${n.totalEarned}:${n.tier}:${n.deactivationTime ?? 0}`
    )
    .join('|');

const formatActiveDuration = (seconds: number) => {
  if (seconds <= 0) return '—';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days > 0) return `${days}d${hours > 0 ? ` ${hours}h` : ''}`;
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

/** Owns the 3s heartbeat so the parent dashboard does not re-render on every tick. */
const TerminalFeed = memo(({ actionLines }: { actionLines: string[] }) => {
  const [simLines, setSimLines] = useState<string[]>([
    '> INITIALIZING NODE EMPIRE...',
    '> LOADING BLOCKCHAIN DATA...',
    '> ESTABLISHING SECURE CONNECTION...',
  ]);
  const terminalOutputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setSimLines((prev) => {
        const next = [...prev];
        if (next.length > 8) next.shift();
        const messages = [
          '> MONITORING NODE PERFORMANCE...',
          '> CALCULATING REWARDS...',
          '> SYNCHRONIZING WITH NETWORK...',
          '> OPTIMIZING HASH POWER...',
          '> CHECKING UPTIME METRICS...',
        ];
        next.push(messages[Math.floor(Math.random() * messages.length)]);
        return next;
      });
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const lines = [...simLines, ...actionLines].slice(-14);

  useEffect(() => {
    const el = terminalOutputRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines.length]);

  return (
    <div className="terminal-output" ref={terminalOutputRef}>
      {lines.map((line, index) => (
        <div key={`${index}-${line}`} className="terminal-line">
          {line}
        </div>
      ))}
    </div>
  );
});
TerminalFeed.displayName = 'TerminalFeed';

export const HackerDashboard: React.FC = () => {
  const { contracts, signer, isConnected, connectWallet, disconnectWallet, currentNetwork, isWrongNetwork, targetNetworkName, switchNetwork } = useNetwork();
  const { t } = useTranslation();
  const networkKey = (import.meta.env.VITE_NETWORK || currentNetwork || 'mordor').toLowerCase();
  const isMordor = networkKey === 'mordor';
  const networkLabel = isMordor ? 'ETC MORDOR' : 'ETC MAINNET';
  const chainIdLabel = isMordor ? '63' : '61';
  const motdMessage = import.meta.env.VITE_MOTD || 'Welcome to Node Empire on Ethereum Classic. Open Shop → buy a starter node → activate → claim ETCPOWv2.';
  const [particlesEnabled, setParticlesEnabled] = useState(() => {
    try {
      const raw = localStorage.getItem('nodeCommander_settings');
      if (!raw) return true;
      return JSON.parse(raw).particleEffectsEnabled !== false;
    } catch {
      return true;
    }
  });

  useEffect(() => {
    const onSettings = (event: Event) => {
      try {
        const detail = (event as CustomEvent).detail;
        if (detail && typeof detail.particleEffectsEnabled === 'boolean') {
          setParticlesEnabled(detail.particleEffectsEnabled !== false);
          return;
        }
        const raw = localStorage.getItem('nodeCommander_settings');
        if (!raw) return;
        setParticlesEnabled(JSON.parse(raw).particleEffectsEnabled !== false);
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('storage', onSettings);
    window.addEventListener('nodeCommander_settings', onSettings as EventListener);
    return () => {
      window.removeEventListener('storage', onSettings);
      window.removeEventListener('nodeCommander_settings', onSettings as EventListener);
    };
  }, []);
  const [nodes, setNodes] = useState<NodeStats[]>([]);
  const nodesFingerprintRef = useRef('');
  const [userBalance, setUserBalance] = useState<string>('0');
  const [userAddress, setUserAddress] = useState<string>('');
  const [selectedNode, setSelectedNode] = useState<NodeStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [pendingRewards, setPendingRewards] = useState<{[key: string]: number}>({});
  const [isOwner, setIsOwner] = useState(false);
  const [gamePaused, setGamePaused] = useState(false);
  const [activeDurationSec, setActiveDurationSec] = useState<number | null>(null);
  // State for storing time remaining for each node
  const [nodeTimeRemaining, setNodeTimeRemaining] = useState<{[nodeId: string]: number}>({});
  const [terminalActionLines, setTerminalActionLines] = useState<string[]>([]);

  const appendTerminal = (line: string) => {
    setTerminalActionLines((prev) => [...prev.slice(-20), line]);
  };

  // Helper function to format time remaining
  const formatTimeRemaining = (seconds: number) => {
    if (seconds <= 0) return 'EXPIRED';
    
    const days = Math.floor(seconds / (24 * 60 * 60));
    const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((seconds % (60 * 60)) / 60);
    
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const isNodeExpired = (node: NodeStats) => {
    const remaining = nodeTimeRemaining[node.tokenId];
    if (remaining !== undefined && remaining !== -1) {
      return node.isActive && remaining <= 0;
    }

    return Boolean(
      node.isActive &&
      node.deactivationTime &&
      Math.floor(Date.now() / 1000) >= node.deactivationTime
    );
  };

  const estimateRewardsUntilExpiry = async (node: NodeStats): Promise<number> => {
    if (!node.isActive || !contracts.nodeNFT || !node.deactivationTime) return 0;
    if (node.deactivationTime <= node.lastClaimTime) return 0;

    try {
      // Prefer exact on-chain pending; estimate is only a last-resort mirror of VirtualNodeNFT math
      try {
        const pending = await contracts.nodeNFT.calculatePendingRewards(node.tokenId);
        if (pending && pending > 0n) {
          return parseFloat(ethers.formatEther(pending));
        }
      } catch {
        /* fall through to local estimate */
      }

      const rewardRatePerHour = await contracts.nodeNFT.rewardRatePerHour();
      const timePassed = BigInt(node.deactivationTime - node.lastClaimTime);
      const totalReward = estimatePendingWei({
        rewardRatePerHourWei: BigInt(rewardRatePerHour.toString()),
        timePassedSec: timePassed,
        tier: node.tier,
        hashPower: node.hashPower,
        bandwidth: node.bandwidth,
        uptime: node.uptime,
        owned: Math.max(nodes.length, 1),
      });

      return parseFloat(ethers.formatEther(totalReward));
    } catch (error) {
      console.error('Failed to estimate expired pending rewards:', error);
      return 0;
    }
  };

  const getNodeStatLimit = (node: NodeStats) => {
    const limitsByTier: Record<number, number> = {
      1: 100,
      2: 250,
      3: 500,
      4: 1000,
      5: 2500,
    };

    return limitsByTier[node.tier] || Math.max(node.hashPower, node.bandwidth, node.uptime, 100);
  };

  const getStatFill = (value: number, node: NodeStats) => {
    return Math.max(4, Math.min(100, Math.round((value / getNodeStatLimit(node)) * 100)));
  };

  // Update time remaining every 30 seconds
  useEffect(() => {
    const updateTimeRemaining = async () => {
      if (!contracts.gameLogic || nodes.length === 0) return;
      
      const newTimeRemaining: {[nodeId: string]: number} = {};
      
      // Check if the contract has the getTimeUntilDeactivation method
      const hasTimeMethod = typeof contracts.gameLogic.getTimeUntilDeactivation === 'function';
      
      if (!hasTimeMethod) {
        // Set all nodes to -1 to indicate timer unavailable
        for (const node of nodes) {
          newTimeRemaining[node.tokenId] = -1;
        }
      } else {
        for (const node of nodes) {
          if (node.isActive) {
            try {
              const timeRemaining = await contracts.gameLogic.getTimeUntilDeactivation(node.tokenId);
              const timeRemainingNum = Number(timeRemaining);
              newTimeRemaining[node.tokenId] = timeRemainingNum;
            } catch (error) {
              console.error('Failed to get time remaining for node:', node.tokenId, error);
              const deactivationTime = Number(node.deactivationTime || 0);
              newTimeRemaining[node.tokenId] =
                deactivationTime > 0
                  ? Math.max(0, deactivationTime - Math.floor(Date.now() / 1000))
                  : -2;
            }
          } else {
            // For inactive nodes, set 0 to avoid "Initializing..." state
            newTimeRemaining[node.tokenId] = 0;
          }
        }
      }
      
      setNodeTimeRemaining(newTimeRemaining);
    };

    // Initial load
    updateTimeRemaining();
    
    // Set up interval
    const interval = setInterval(updateTimeRemaining, 30000);

    return () => clearInterval(interval);
  }, [nodes, contracts.gameLogic]);
  
  // Panel state
  const [showInventory, setShowInventory] = useState(false);
  const [inventoryFocusNodeId, setInventoryFocusNodeId] = useState<string | null>(null);
  const [showAchievements, setShowAchievements] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showReferral, setShowReferral] = useState(false);
  const [showShop, setShowShop] = useState(false);
  const [showDailyRewards, setShowDailyRewards] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showBoostEvents, setShowBoostEvents] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'online' | 'offline'>('all');
  const [sortBy, setSortBy] = useState<'hashrate' | 'uptime' | 'id'>('id');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const closeAllPanels = () => {
    setShowInventory(false);
    setShowAchievements(false);
    setShowLeaderboard(false);
    setShowReferral(false);
    setShowShop(false);
    setShowDailyRewards(false);
    setShowAnalytics(false);
    setShowSettings(false);
    setShowHelp(false);
    setShowBoostEvents(false);
    setShowAdminPanel(false);
  };

  useEffect(() => {
    const handleOpenInventory = (event: Event) => {
      const detail = (event as CustomEvent<{ nodeId?: string }>).detail;
      if (detail?.nodeId) {
        setInventoryFocusNodeId(detail.nodeId);
      }
      setShowAchievements(false);
      setShowLeaderboard(false);
      setShowReferral(false);
      setShowShop(false);
      setShowDailyRewards(false);
      setShowAnalytics(false);
      setShowSettings(false);
      setShowHelp(false);
      setShowBoostEvents(false);
      setShowAdminPanel(false);
      setShowInventory(true);
    };
    window.addEventListener('openInventory', handleOpenInventory);
    return () => window.removeEventListener('openInventory', handleOpenInventory);
  }, []);

  const openPanel = (panel: string) => {
    closeAllPanels();
    switch (panel) {
      case 'inventory': setShowInventory(true); break;
      case 'achievements': setShowAchievements(true); break;
      case 'shop': setShowShop(true); break;
      case 'leaderboard': setShowLeaderboard(true); break;
      case 'referral': setShowReferral(true); break;
      case 'daily': setShowDailyRewards(true); break;
      case 'season': setShowBoostEvents(true); break;
      case 'settings': setShowSettings(true); break;
      case 'help': setShowHelp(true); break;
      case 'analytics': setShowAnalytics(true); break;
      case 'admin': setShowAdminPanel(true); break;
      default: break;
    }
  };

  const handleOverlayPointer = (event: React.MouseEvent<HTMLDivElement>, close: () => void) => {
    if (event.target === event.currentTarget) {
      close();
    }
  };

  useEffect(() => {
    const anyOpen =
      showInventory ||
      showAchievements ||
      showLeaderboard ||
      showReferral ||
      showShop ||
      showDailyRewards ||
      showAnalytics ||
      showSettings ||
      showHelp ||
      showBoostEvents ||
      showAdminPanel;

    if (!anyOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeAllPanels();
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [
    showInventory,
    showAchievements,
    showLeaderboard,
    showReferral,
    showShop,
    showDailyRewards,
    showAnalytics,
    showSettings,
    showHelp,
    showBoostEvents,
    showAdminPanel,
  ]);

  useEffect(() => {
    if (isConnected && contracts.etcpowv2Token) {
      void loadUserData();
      
      // Soft balance/fleet refresh — pending rewards have their own lighter poll
      const interval = setInterval(() => {
        void loadUserData({ soft: true });
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [isConnected, contracts.etcpowv2Token, contracts.nodeNFT, contracts.gameLogic]);

  useEffect(() => {
    const pollPaused = async () => {
      if (!contracts.gameLogic) {
        setGamePaused(false);
        return;
      }
      try {
        const paused = await contracts.gameLogic.paused();
        setGamePaused(Boolean(paused));
      } catch {
        setGamePaused(false);
      }
    };

    void pollPaused();
    if (!contracts.gameLogic) return undefined;
    const interval = setInterval(() => {
      void pollPaused();
    }, 30000);
    return () => clearInterval(interval);
  }, [contracts.gameLogic]);

  useEffect(() => {
    const loadActiveDuration = async () => {
      if (!contracts.nodeNFT) {
        setActiveDurationSec(null);
        return;
      }
      try {
        const duration = await contracts.nodeNFT.activeDuration();
        setActiveDurationSec(Number(duration));
      } catch {
        setActiveDurationSec(null);
      }
    };
    void loadActiveDuration();
  }, [contracts.nodeNFT]);

  // Listen for purchase events to refresh data
  useEffect(() => {
    const handleItemPurchased = () => {
      void loadUserData({ soft: true });
    };
    
    window.addEventListener('itemPurchased', handleItemPurchased);
    
    return () => {
      window.removeEventListener('itemPurchased', handleItemPurchased);
    };
  }, [isConnected, contracts]);

  const loadUserData = async (opts?: { soft?: boolean }) => {
    if (!isConnected || !signer || !contracts.nodeNFT) return;

    try {
      if (!opts?.soft) setLoading(true);
      const address = await signer.getAddress();
      setUserAddress(address);
      
      // Resolve owner from contract (fallback to known Mordor deployer)
      let isUserOwner = false;
      try {
        if (contracts.gameLogic?.owner) {
          const onChainOwner = await contracts.gameLogic.owner();
          isUserOwner = address.toLowerCase() === String(onChainOwner).toLowerCase();
        }
      } catch {
        isUserOwner = false;
      }
      setIsOwner(isUserOwner);
      
      if (isUserOwner && !opts?.soft) {
        appendTerminal('> OWNER ACCESS GRANTED');
      }
      
      // Load ETCPOWv2 balance
      try {
        const balance = await contracts.etcpowv2Token.balanceOf(address);
        const formattedBalance = formatToken(balance.toString());
        setUserBalance(formattedBalance);
      } catch (tokenError) {
        console.error('Failed to load ETCPOWv2 balance:', tokenError);
        setUserBalance('0');
      }
      
      // Load user's nodes (batch id fetch + parallel stats — RPC friendly)
      if (contracts.nodeNFT) {
        try {
          let tokenIds: any[] = [];
          try {
            tokenIds = await contracts.nodeNFT.getNodesByOwner(address);
          } catch {
            const nodeBalance = await contracts.nodeNFT.balanceOf(address);
            const count = Number(nodeBalance);
            tokenIds = await Promise.all(
              Array.from({ length: count }, (_, i) =>
                contracts.nodeNFT.tokenOfOwnerByIndex(address, i)
              )
            );
          }

          const nodeIds = await Promise.all(
            tokenIds.map(async (tokenIdRaw) => {
              const tokenId = tokenIdRaw.toString();
              const stats = await contracts.nodeNFT.getNodeStats(tokenId);
              return {
                tokenId,
                hashPower: Number(stats.hashPower),
                bandwidth: Number(stats.bandwidth),
                uptime: Number(stats.uptime),
                lastClaimTime: Number(stats.lastClaimTime),
                totalEarned: Number(stats.totalEarned),
                tier: Number(stats.tier),
                isActive: stats.isActive,
                activationTime: Number(stats.activationTime || 0),
                deactivationTime: Number(stats.deactivationTime || 0),
              };
            })
          );

          const fingerprint = nodesFingerprint(nodeIds);
          if (!opts?.soft || fingerprint !== nodesFingerprintRef.current) {
            nodesFingerprintRef.current = fingerprint;
            setNodes(nodeIds);
          }
          if (!opts?.soft) {
            appendTerminal(`> LOADED ${nodeIds.length} NODES FROM BLOCKCHAIN`);
          }
        } catch (nodeError) {
          console.error('Failed to load nodes:', nodeError);
          if (!opts?.soft) setNodes([]);
        }
      } else if (!opts?.soft) {
        setNodes([]);
      }
      
    } catch (error) {
      console.error('Failed to load user data:', error);
      if (!opts?.soft) {
        notify.error(error, 'Failed to load game data');
        appendTerminal('> ERROR: FAILED TO LOAD BLOCKCHAIN DATA');
      }
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  };

  const assertPlayable = () => {
    if (isWrongNetwork) {
      notify.error(`Wrong network — switch to ${targetNetworkName}`);
      return false;
    }
    if (!contracts.gameLogic) {
      notify.error(null, 'Game contracts unavailable');
      return false;
    }
    if (gamePaused) {
      notify.error('Game is paused by owner — actions are disabled');
      return false;
    }
    return true;
  };

  const toggleNodeActive = async (nodeId: string) => {
    if (!assertPlayable()) return;

    try {
      setLoading(true);

      // Charge reactivationFee on inactive→active; ensure allowance before toggle
      try {
        const node = nodes.find((n: any) => String(n.tokenId) === String(nodeId));
        const activating = node && !node.isActive;
        if (activating && contracts.etcpowv2Token && contracts.gameLogic) {
          const fee: bigint = await contracts.gameLogic.reactivationFee();
          if (fee > 0n) {
            const spender = await contracts.gameLogic.getAddress();
            const allowance: bigint = await contracts.etcpowv2Token.allowance(
              await signer!.getAddress(),
              spender
            );
            if (allowance < fee) {
              notify.loading('Approving reactivation fee...', { id: 'toggle' });
              const approveTx = await contracts.etcpowv2Token.approve(spender, fee);
              await approveTx.wait();
            }
          }
        }
      } catch {
        // Older deployments may lack reactivationFee
      }

      const tx = await contracts.gameLogic.toggleNodeActive(nodeId);
      notify.loading(t('toast.toggling_node'), { id: 'toggle' });
      
      await tx.wait();
      notify.success(t('toast.node_updated'), { id: 'toggle' });
      appendTerminal(`> NODE ${nodeId} STATUS UPDATED`);
      
      await loadUserData();
    } catch (error: any) {
      console.error('Failed to toggle node:', error);
      notify.error(error, 'Could not update node status — check pool balance / reactivation fee allowance', { id: 'toggle' });
      appendTerminal('> ERROR: NODE STATUS UPDATE FAILED');
    } finally {
      setLoading(false);
    }
  };

  const claimRewards = async (nodeId: string) => {
    if (!assertPlayable()) return;

    try {
      setLoading(true);
      const tx = await contracts.gameLogic.claimNodeRewards(nodeId);
      notify.loading(t('toast.claiming_rewards'), { id: 'claim' });
      
      await tx.wait();
      notify.success(t('toast.rewards_claimed'), { id: 'claim' });
      appendTerminal(`> CLAIMED REWARDS FROM NODE ${nodeId}`);
      
      await loadUserData();
    } catch (error: any) {
      console.error('Failed to claim rewards:', error);
      notify.error(error, 'Could not claim rewards — rewards pool may be empty', { id: 'claim' });
      appendTerminal('> ERROR: REWARD CLAIM FAILED');
    } finally {
      setLoading(false);
    }
  };

  const claimAllRewards = async () => {
    if (!assertPlayable()) return;

    const claimable = nodes.filter(
      (node) => node.isActive && (pendingRewards[node.tokenId] || 0) > 0
    );

    if (claimable.length === 0) {
      notify.info('No pending rewards to claim');
      return;
    }

    try {
      setLoading(true);
      notify.loading(`Claiming ${claimable.length} node(s)...`, { id: 'claim-all' });

      const ids = claimable.map((n) => n.tokenId);
      let claimed = 0;

      for (let i = 0; i < ids.length; i += CLAIM_CHUNK_SIZE) {
        const chunk = ids.slice(i, i + CLAIM_CHUNK_SIZE);
        let batchOk = false;

        try {
          if (contracts.gameLogic.claimMultipleNodeRewards?.staticCall) {
            await contracts.gameLogic.claimMultipleNodeRewards.staticCall(chunk);
          }
          const tx = await contracts.gameLogic.claimMultipleNodeRewards(chunk);
          await tx.wait();
          batchOk = true;
          claimed += chunk.length;
        } catch {
          batchOk = false;
        }

        if (!batchOk) {
          for (const id of chunk) {
            try {
              const tx = await contracts.gameLogic.claimNodeRewards(id);
              await tx.wait();
              claimed += 1;
            } catch (seqError) {
              console.error(`Failed to claim node ${id}:`, seqError);
            }
          }
        }
      }

      if (claimed === 0) {
        throw new Error('No claims succeeded');
      }

      appendTerminal(`> CLAIMED REWARDS FROM ${claimed} NODE(S)`);
      notify.success(`Claimed rewards from ${claimed} node(s)`, { id: 'claim-all' });
      await loadUserData({ soft: true });
    } catch (error: any) {
      console.error('Failed to claim all rewards:', error);
      notify.error(error, 'Could not claim all rewards', { id: 'claim-all' });
      appendTerminal('> ERROR: CLAIM ALL FAILED');
    } finally {
      setLoading(false);
    }
  };

  const calculatePendingRewards = async (node: NodeStats): Promise<number> => {
    if (!node.isActive || !contracts.nodeNFT) return 0;
    
    try {
      // Contract-only source of truth for pending rewards
      const pendingRewardsWei = await contracts.nodeNFT.calculatePendingRewards(node.tokenId);
      const pending = parseFloat(ethers.formatEther(pendingRewardsWei));

      // Older deployed contracts returned 0 after expiry even though rewards
      // accrued until the expiry timestamp. Show the recoverable amount so the
      // UI does not make it look like prior rewards were deleted.
      if (pending === 0 && isNodeExpired(node)) {
        return estimateRewardsUntilExpiry(node);
      }

      return pending;
    } catch (error) {
      console.error('Failed to calculate pending rewards from contract:', error);
      return 0;
    }
  };

  const updatePendingRewards = async () => {
    if (!contracts.nodeNFT || nodes.length === 0) return;

    const entries = await Promise.all(
      nodes.map(async (node) => {
        try {
          const reward = await calculatePendingRewards(node);
          return [node.tokenId, reward] as const;
        } catch (error) {
          console.error(`Failed to calculate rewards for node ${node.tokenId}:`, error);
          return [node.tokenId, 0] as const;
        }
      })
    );

    setPendingRewards(Object.fromEntries(entries));
  };

  // Filter and sort nodes
  const getFilteredAndSortedNodes = () => {
    let filteredNodes = [...nodes];
    
    // Apply search filter
    if (searchTerm) {
      filteredNodes = filteredNodes.filter(node => 
        node.tokenId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `NODE-${node.tokenId}`.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply status filter
    if (filterStatus === 'online') {
      filteredNodes = filteredNodes.filter(node => node.isActive);
    } else if (filterStatus === 'offline') {
      filteredNodes = filteredNodes.filter(node => !node.isActive);
    }
    
    // Apply sorting
    filteredNodes.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'hashrate':
          comparison = a.hashPower - b.hashPower;
          break;
        case 'uptime':
          comparison = a.uptime - b.uptime;
          break;
        case 'id':
        default:
          comparison = parseInt(a.tokenId) - parseInt(b.tokenId);
          break;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return filteredNodes;
  };

  // Update pending rewards when nodes change
  useEffect(() => {
    if (nodes.length > 0) {
      updatePendingRewards();
    }
  }, [nodes]);

  // Periodic refresh for pending rewards
  useEffect(() => {
    if (isConnected && contracts.nodeNFT && nodes.length > 0) {
      const interval = setInterval(() => {
        updatePendingRewards();
      }, 30000); // Update every 30 seconds
      
      return () => clearInterval(interval);
    }
  }, [isConnected, contracts.nodeNFT, nodes.length]);

  const handleConnectWallet = async () => {
    try {
      await connectWallet();
      notify.success('Wallet connected — loading empire...');
      await loadUserData();
    } catch (error: any) {
      console.error('Failed to connect wallet:', error);
      if (error.code === 4001) {
        notify.error(null, 'Connection cancelled');
      } else if (error.code === -32002) {
        notify.error(null, 'Unlock your wallet and try again');
      } else {
        notify.error(error, 'Could not connect wallet');
      }
    }
  };

  if (!isConnected) {
    return (
      <div className="hacker-dashboard">
        <div className="terminal-header">
          <div className="header-left">
            <div className="terminal-title">NODE EMPIRE</div>
            <div className="header-subtitle">Ethereum Classic · ETCMCv2</div>
          </div>
          <div className="header-center">
            <div className="network-status">
              <span className="network-label">NETWORK</span>
              <span className="network-value">{networkLabel}</span>
              <span className="network-chip">#{chainIdLabel}</span>
              <span className="network-indicator online"></span>
            </div>
          </div>
          <div className="header-right">
            <button onClick={handleConnectWallet} className="header-connect-btn">
              Connect Wallet
            </button>
          </div>
        </div>

        <div className="connect-gate">
          <div className="connect-gate-card">
            <p className="connect-gate-kicker">Virtual Node Empire</p>
            <h2 className="connect-gate-title">Command your fleet on ETC</h2>
            <p className="connect-gate-lead">
              Buy nodes, apply upgrades, open chests, and claim ETCPOWv2 rewards — fully on-chain on Ethereum Classic.
            </p>
            <ol className="connect-gate-steps">
              <li>Connect a wallet and switch to {isMordor ? 'Mordor (63)' : 'ETC Mainnet (61)'}.</li>
              <li>Open Shop → purchase a starter node with ETCPOWv2.</li>
              <li>Activate the node, then claim rewards as they accrue.</li>
            </ol>
            <button onClick={handleConnectWallet} className="connect-gate-cta">
              Connect &amp; Enter Command Deck
            </button>
            <p className="connect-gate-note">{motdMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hacker-dashboard">
      <div className="terminal-header">
        <div className="header-left">
          <div className="terminal-title">NODE EMPIRE</div>
          <div className="header-subtitle">Ethereum Classic · ETCMCv2</div>
        </div>
        
        <div className="header-center">
          <div className="network-status">
            <span className="network-label">NETWORK</span>
            <span className="network-value">{networkLabel}</span>
            <span className="network-chip">#{chainIdLabel}</span>
            <span className="network-indicator online"></span>
          </div>
        </div>
        
        <div className="header-right">
          <div className="balance-display">
            <span className="balance-label">BALANCE</span>
            <span className="balance-value">{formatToken(userBalance)} ETCPOWv2</span>
          </div>
          <div className="wallet-section">
            {!isConnected ? (
              <button
                type="button"
                onClick={async () => {
                  try {
                    await connectWallet();
                    notify.success('Wallet connected');
                    await loadUserData();
                  } catch (error: any) {
                    console.error('Failed to connect wallet:', error);
                    if (error.code === 4001) {
                      notify.error(null, 'Connection cancelled');
                    } else if (error.code === -32002) {
                      notify.error(null, 'Unlock your wallet and try again');
                    } else {
                      notify.error(error, 'Could not connect wallet');
                    }
                  }
                }}
                className="header-connect-btn"
              >
                {t('dashboard.connect_wallet')}
              </button>
            ) : (
              <div className="header-wallet-info">
                <span className="wallet-address" title={userAddress || undefined}>
                  {userAddress ? `${userAddress.slice(0, 6)}…${userAddress.slice(-4)}` : 'Connecting…'}
                </span>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      setUserAddress('');
                      setUserBalance('0');
                      setNodes([]);
                      disconnectWallet();
                    } catch (error: any) {
                      console.error('Failed to disconnect wallet:', error);
                      notify.error(error, 'Could not disconnect wallet');
                    }
                  }}
                  className="wallet-disconnect-btn"
                >
                  {t('dashboard.disconnect')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="motd-banner">
        <span className="motd-label">System Notice</span>
        <span className="motd-text">{motdMessage}</span>
      </div>

      {gamePaused && (
        <div className="paused-banner" role="status">
          <span className="paused-label">PAUSED</span>
          <span>GameLogic is paused — purchases, opens, claims, and toggles are locked until the owner unpauses.</span>
        </div>
      )}

      <div
        className={`wrong-network-banner ${isWrongNetwork ? 'is-visible' : 'is-placeholder'}`}
        aria-hidden={!isWrongNetwork}
      >
        {isWrongNetwork ? (
          <>
            <span>Wrong chain — switch to {targetNetworkName} (Ethereum Classic).</span>
            <button type="button" onClick={() => switchNetwork()}>Switch Network</button>
          </>
        ) : (
          <span className="wrong-network-spacer">Network OK</span>
        )}
      </div>

      <div className="command-strip">
        <div className="command-metric primary">
          <span className="command-label">Fleet</span>
          <strong>{loading && !hasLoadedOnce ? '—' : nodes.length}</strong>
          <span>{nodes.filter(n => n.isActive).length} online</span>
        </div>
        <div className="command-metric">
          <span className="command-label">Hash Output</span>
          <strong>
            {loading && !hasLoadedOnce
              ? '—'
              : formatHashPower(nodes.reduce((sum, n) => sum + n.hashPower, 0))}
          </strong>
          <span>combined node power</span>
        </div>
        <div className="command-metric">
          <span className="command-label">Pending</span>
          <strong>
            {loading && !hasLoadedOnce
              ? '—'
              : formatSmallAmount(nodes.reduce((sum, n) => sum + (pendingRewards[n.tokenId] || 0), 0))}
          </strong>
          <span>ETCPOWv2 ready</span>
        </div>
        <div className="command-metric wallet">
          <span className="command-label">Wallet</span>
          <strong className="command-balance">{formatToken(userBalance)}</strong>
          <span>{userAddress ? `${userAddress.slice(0, 6)}...${userAddress.slice(-4)}` : 'not connected'}</span>
        </div>
        <button
          type="button"
          className={`command-claim-all ${
            nodes.some((n) => n.isActive && (pendingRewards[n.tokenId] || 0) > 0) ? '' : 'is-disabled'
          }`}
          disabled={!nodes.some((n) => n.isActive && (pendingRewards[n.tokenId] || 0) > 0)}
          onClick={claimAllRewards}
        >
          Claim All
        </button>
      </div>

      <div className="main-navigation">
        <div className="nav-container">
          <button
            type="button"
            className={`nav-btn ${showInventory ? 'active' : ''}`}
            onClick={() => (showInventory ? closeAllPanels() : openPanel('inventory'))}
          >
            {t('nav.inventory')}
          </button>
          <button
            type="button"
            className={`nav-btn ${showAchievements ? 'active' : ''}`}
            onClick={() => (showAchievements ? closeAllPanels() : openPanel('achievements'))}
          >
            {t('nav.achievements')}
          </button>
          <button
            type="button"
            className={`nav-btn ${showShop ? 'active' : ''}`}
            onClick={() => (showShop ? closeAllPanels() : openPanel('shop'))}
          >
            {t('nav.shop')}
          </button>
          <button
            type="button"
            className={`nav-btn ${showLeaderboard ? 'active' : ''}`}
            onClick={() => (showLeaderboard ? closeAllPanels() : openPanel('leaderboard'))}
          >
            {t('nav.leaderboard')}
          </button>
          <button
            type="button"
            className={`nav-btn ${showReferral ? 'active' : ''}`}
            onClick={() => (showReferral ? closeAllPanels() : openPanel('referral'))}
          >
            {t('nav.referral')}
          </button>
          <button
            type="button"
            className={`nav-btn ${showDailyRewards ? 'active' : ''}`}
            onClick={() => (showDailyRewards ? closeAllPanels() : openPanel('daily'))}
          >
            {t('nav.daily')}
          </button>
          <button
            type="button"
            className={`nav-btn ${showBoostEvents ? 'active' : ''}`}
            onClick={() => (showBoostEvents ? closeAllPanels() : openPanel('season'))}
          >
            Season
          </button>
          <button
            type="button"
            className={`nav-btn ${showSettings ? 'active' : ''}`}
            onClick={() => (showSettings ? closeAllPanels() : openPanel('settings'))}
          >
            {t('nav.settings')}
          </button>
          <button
            type="button"
            className={`nav-btn ${showAnalytics ? 'active' : ''}`}
            onClick={() => (showAnalytics ? closeAllPanels() : openPanel('analytics'))}
          >
            Analytics
          </button>
          <button
            type="button"
            className={`nav-btn admin-btn ${showAdminPanel ? 'active' : ''} ${isOwner ? '' : 'is-slot'}`}
            onClick={() => {
              if (!isOwner) return;
              showAdminPanel ? closeAllPanels() : openPanel('admin');
            }}
            aria-hidden={!isOwner}
            tabIndex={isOwner ? 0 : -1}
          >
            Admin
          </button>
          <button
            type="button"
            className={`nav-btn ${showHelp ? 'active' : ''}`}
            onClick={() => (showHelp ? closeAllPanels() : openPanel('help'))}
          >
            {t('nav.help')}
          </button>
        </div>
      </div>

      <div className="dashboard-grid">
        {/* Left Panel - Node List */}
        <div className="panel left-panel">
          <div className="panel-header nc-header">
            <div>
              <div className="panel-title">NODE CONTROL</div>
              <div className="nc-subtitle">Fleet management</div>
            </div>
            <div className="panel-stats nc-stats">
              <span className="nc-chip">{nodes.length} nodes</span>
              <span className="nc-chip accent">{nodes.filter((n) => n.isActive).length} online</span>
            </div>
          </div>
          
          {/* Search and Filters */}
          <div className="search-filters-container">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search by ID or tier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            
            <div className="filter-controls">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as 'all' | 'online' | 'offline')}
                className="filter-select"
              >
                <option value="all">All</option>
                <option value="online">Online</option>
                <option value="offline">Offline</option>
              </select>
              
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [sort, order] = e.target.value.split('-');
                  setSortBy(sort as 'hashrate' | 'uptime' | 'id');
                  setSortOrder(order as 'asc' | 'desc');
                }}
                className="filter-select"
              >
                <option value="id-asc">ID ↑</option>
                <option value="id-desc">ID ↓</option>
                <option value="hashrate-desc">Hash ↓</option>
                <option value="hashrate-asc">Hash ↑</option>
                <option value="uptime-desc">Uptime ↓</option>
                <option value="uptime-asc">Uptime ↑</option>
              </select>
            </div>
          </div>
          
          <div className="node-list">
            {loading && !hasLoadedOnce ? (
              [0, 1, 2].map((slot) => (
                <div key={`sk-${slot}`} className="node-card node-skeleton" aria-hidden="true">
                  <div className="sk-row">
                    <div className="loading-skeleton sk-chip" />
                    <div className="loading-skeleton sk-chip short" />
                  </div>
                  <div className="loading-skeleton sk-bar" />
                  <div className="loading-skeleton sk-bar" />
                  <div className="loading-skeleton sk-bar" />
                  <div className="loading-skeleton sk-block" />
                  <div className="sk-row">
                    <div className="loading-skeleton sk-btn" />
                    <div className="loading-skeleton sk-btn" />
                  </div>
                </div>
              ))
            ) : getFilteredAndSortedNodes().length > 0 ? (
              getFilteredAndSortedNodes().map(node => {
                const remaining = nodeTimeRemaining[node.tokenId];
                const timerUnavailable = remaining === -1;
                const timerSyncError = remaining === -2;
                const expired = remaining === 0;
                const activeWithTimer = remaining !== undefined && remaining > 0;
                const pending = pendingRewards[node.tokenId] || 0;

                return (
                <div 
                  key={node.tokenId}
                  className={`node-card ${selectedNode?.tokenId === node.tokenId ? 'selected' : ''} ${node.isActive ? 'is-online' : 'is-offline'} ${expired ? 'is-expired' : ''}`}
                  onClick={() => setSelectedNode(node)}
                >
                <div className="node-header">
                  <div className="node-header-main">
                    <span className="node-id">NODE-{node.tokenId}</span>
                    <span className={`tier-badge ${getTierColor(node.tier)}`}>
                      T{node.tier}
                    </span>
                  </div>
                  <span className={`node-status ${node.isActive ? 'active' : 'inactive'}`}>
                    {node.isActive ? t('network.online') : t('network.offline')}
                  </span>
                </div>

                <div className="node-vitals">
                  <div className="vital-meter">
                    <span>HASH</span>
                    <div className="meter-track">
                      <div className="meter-fill hash" style={{ width: `${getStatFill(node.hashPower, node)}%` }} />
                    </div>
                    <span className="vital-value">{formatHashPower(node.hashPower)}</span>
                  </div>
                  <div className="vital-meter">
                    <span>BAND</span>
                    <div className="meter-track">
                      <div className="meter-fill band" style={{ width: `${getStatFill(node.bandwidth, node)}%` }} />
                    </div>
                    <span className="vital-value">{formatBandwidth(node.bandwidth)}</span>
                  </div>
                  <div className="vital-meter">
                    <span>UP</span>
                    <div className="meter-track">
                      <div className="meter-fill uptime" style={{ width: `${getStatFill(node.uptime, node)}%` }} />
                    </div>
                    <span className="vital-value">{formatUptime(node.uptime)}</span>
                  </div>
                </div>

                {node.isActive && (
                  <div className={`countdown-container ${expired ? 'is-expired' : ''}`}>
                    <div className="countdown-label">
                      {activeWithTimer
                        ? 'AUTO-OFF'
                        : timerUnavailable
                        ? 'LIVE'
                        : timerSyncError
                        ? 'SYNC ERROR'
                        : expired
                        ? 'EXPIRED — CLAIM & DEACTIVATE'
                        : 'SYNCING'}
                    </div>
                    <div className={`countdown-timer ${expired ? 'expired' : ''}`}>
                      {activeWithTimer
                        ? formatTimeRemaining(remaining)
                        : timerUnavailable
                        ? 'ACTIVE'
                        : timerSyncError
                        ? 'RETRYING…'
                        : expired
                        ? '—'
                        : '...'}
                    </div>
                    {activeDurationSec != null && activeWithTimer && (
                      <div className="countdown-duration-hint">
                        Window {formatActiveDuration(activeDurationSec)}
                      </div>
                    )}
                  </div>
                )}
                
                <div className="node-rewards">
                  <span className="rewards-label">{t('nodes.pending')}</span>
                  <span className="rewards-value">
                    {formatSmallAmount(pending)} <small>ETCPOWv2</small>
                  </span>
                </div>
                
                <div className="node-actions">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleNodeActive(node.tokenId);
                    }}
                    className={`hacker-button small ${node.isActive && !isNodeExpired(node) ? 'danger' : 'success'}`}
                  >
                    {isNodeExpired(node) ? 'CLAIM & DEACTIVATE' : node.isActive ? t('nodes.deactivate') : t('nodes.activate')}
                  </button>
                  {pending > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        claimRewards(node.tokenId);
                      }}
                      className="hacker-button small success"
                    >
                      {t('nodes.claim')}
                    </button>
                  )}
                  <button
                    type="button"
                    className="hacker-button small ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedNode(node);
                      openPanel('inventory');
                    }}
                    title="Apply upgrade"
                  >
                    UPGRADE
                  </button>
                </div>
              </div>
              );
              })
            ) : (
              <div className="empty-state fleet-empty">
                <div className="empty-icon" aria-hidden="true">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                    <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="empty-text">
                  {searchTerm || filterStatus !== 'all' ? 'NO MATCHING NODES' : 'FLEET EMPTY'}
                </div>
                <div className="empty-subtext">
                  {searchTerm || filterStatus !== 'all'
                    ? 'Try adjusting your search or filters'
                    : 'Buy a starter node with ETCPOWv2, activate it, and start earning.'}
                </div>
                {!(searchTerm || filterStatus !== 'all') && (
                  <button
                    type="button"
                    className="fleet-empty-cta"
                    onClick={() => openPanel('shop')}
                  >
                    Open Shop — Buy Starter Node
                  </button>
                )}
              </div>
            )}
          </div>
          
          <div className="panel-actions" />
        </div>

        {/* Center Panel - Tree Map */}
        <div className="panel center-panel">
          <div className="panel-header">
            <div className="panel-title">{t('network.topology')}</div>
            <div className="panel-stats">
              <span className="stat-item">
                {t('network.active')}: {nodes.filter(n => n.isActive).length}/{nodes.length}
              </span>
            </div>
          </div>
          
          <div className="map-container">
            <NodeTreeMap 
              nodes={nodes} 
              onNodeClick={setSelectedNode}
              selectedNode={selectedNode}
              pendingRewards={pendingRewards}
            />
          </div>
        </div>

        {/* Right Panel - Terminal */}
        <div className="panel right-panel terminal-panel">
          <div className="terminal-panel-header">
            <div className="terminal-panel-brand">
              <span className="terminal-panel-kicker">SYS</span>
              <div className="terminal-panel-title">{t('network.terminal')}</div>
              <span className="terminal-panel-live">
                <span className="terminal-panel-live-dot" aria-hidden="true" />
                LIVE
              </span>
            </div>
            <div className="terminal-metric-grid">
              <div className="terminal-metric">
                <span className="terminal-metric-label">Hash</span>
                <span className="terminal-metric-value">
                  {formatHashPower(nodes.reduce((sum, n) => sum + n.hashPower, 0))}
                </span>
              </div>
              <div className="terminal-metric">
                <span className="terminal-metric-label">Band</span>
                <span className="terminal-metric-value">
                  {formatBandwidth(nodes.reduce((sum, n) => sum + n.bandwidth, 0))}
                </span>
              </div>
              <div className="terminal-metric">
                <span className="terminal-metric-label">Nodes</span>
                <span className="terminal-metric-value">
                  {nodes.filter((n) => n.isActive).length}/{nodes.length}
                </span>
              </div>
              {activeDurationSec != null && (
                <div className="terminal-metric">
                  <span className="terminal-metric-label">Window</span>
                  <span className="terminal-metric-value">{formatActiveDuration(activeDurationSec)}</span>
                </div>
              )}
              <div className="terminal-metric terminal-metric-wide">
                <span className="terminal-metric-label">Pending</span>
                <span className="terminal-metric-value accent">
                  {formatSmallAmount(nodes.reduce((sum, n) => sum + (pendingRewards[n.tokenId] || 0), 0))}
                  <small>ETCPOWv2</small>
                </span>
              </div>
            </div>
          </div>

          <div className="terminal-feed-shell">
            <div className="terminal-feed-bar">
              <span>SESSION LOG</span>
              <span className="terminal-feed-bar-hint">scroll for history</span>
            </div>
            <TerminalFeed actionLines={terminalActionLines} />
          </div>

          <div className="terminal-tx-shell">
            <TransactionStatus />
          </div>
          
          {selectedNode && (
            <div className="selected-node-info">
              <div className="info-header">{t('network.selected_node')}</div>
              <div className="info-content">
                <div className="info-line">
                  <span className="info-label">{t('network.node_id')}</span>
                  <span className="info-value">{selectedNode.tokenId}</span>
                </div>
                <div className="info-line">
                  <span className="info-label">{t('nodes.tier')}</span>
                  <span className={`info-value ${getTierColor(selectedNode.tier)}`}>
                    {getTierName(selectedNode.tier)}
                  </span>
                </div>
                <div className="info-line">
                  <span className="info-label">{t('network.status')}:</span>
                  <span className={`info-value ${selectedNode.isActive ? 'text-green-400' : 'text-red-400'}`}>
                    {selectedNode.isActive ? t('network.online') : t('network.offline')}
                  </span>
                </div>
                <div className="info-line">
                  <span className="info-label">{t('network.hash_power')}</span>
                  <span className="info-value text-cyan-400">
                    {formatHashPower(selectedNode.hashPower)}
                  </span>
                </div>
                <div className="info-line">
                  <span className="info-label">{t('network.bandwidth')}</span>
                  <span className="info-value text-blue-400">
                    {formatBandwidth(selectedNode.bandwidth)}
                  </span>
                </div>
                <div className="info-line">
                  <span className="info-label">{t('network.uptime')}</span>
                  <span className="info-value text-yellow-400">
                    {formatUptime(selectedNode.uptime)}
                  </span>
                </div>
                <div className="info-line">
                  <span className="info-label">{t('network.pending')}</span>
                  <span className="info-value text-green-400">
                    {formatSmallAmount(pendingRewards[selectedNode.tokenId] || 0)} ETCPOWv2
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Effects Canvas */}
      {particlesEnabled && <EffectsCanvas />}
      
      {/* Effects Display */}
      {particlesEnabled && <EffectsDisplay />}
      {/* Panel Overlays — click backdrop or press Escape to close */}
      {showInventory && (
        <div
          className="panel-overlay inventory-overlay"
          onClick={(e) => handleOverlayPointer(e, () => setShowInventory(false))}
        >
          <div className="modal-shell inventory-modal-shell">
            <InventoryPanel 
              onClose={() => {
                setShowInventory(false);
                setInventoryFocusNodeId(null);
              }}
              initialSelectedNodeId={inventoryFocusNodeId || selectedNode?.tokenId || null}
            />
          </div>
        </div>
      )}

      {showAchievements && (
        <div
          className="panel-overlay"
          onClick={(e) => handleOverlayPointer(e, () => setShowAchievements(false))}
        >
          <AchievementsPanel onClose={() => setShowAchievements(false)} />
        </div>
      )}

      {showShop && (
        <div
          className="panel-overlay"
          onClick={(e) => handleOverlayPointer(e, () => setShowShop(false))}
        >
          <div className="modal-shell">
            <ShopPanel onClose={() => setShowShop(false)} balance={formatToken(userBalance)} />
          </div>
        </div>
      )}

      {showLeaderboard && (
        <div
          className="panel-overlay"
          onClick={(e) => handleOverlayPointer(e, () => setShowLeaderboard(false))}
        >
          <div className="modal-shell leaderboard-modal-shell">
            <LeaderboardPanel onClose={() => setShowLeaderboard(false)} />
          </div>
        </div>
      )}

      {showReferral && (
        <div
          className="panel-overlay"
          onClick={(e) => handleOverlayPointer(e, () => setShowReferral(false))}
        >
          <div className="modal-shell">
            <ReferralPanel onClose={() => setShowReferral(false)} />
          </div>
        </div>
      )}

      {showSettings && (
        <div
          className="panel-overlay"
          onClick={(e) => handleOverlayPointer(e, () => setShowSettings(false))}
        >
          <div className="modal-shell">
            <SettingsPanel onClose={() => setShowSettings(false)} />
          </div>
        </div>
      )}

      {showAnalytics && (
        <div
          className="panel-overlay"
          onClick={(e) => handleOverlayPointer(e, () => setShowAnalytics(false))}
        >
          <div className="modal-shell">
            <AnalyticsPanel onClose={() => setShowAnalytics(false)} />
          </div>
        </div>
      )}

      {showAdminPanel && isOwner && (
        <div
          className="panel-overlay"
          onClick={(e) => handleOverlayPointer(e, () => setShowAdminPanel(false))}
        >
          <div className="modal-shell">
            <AdminPanel onClose={() => setShowAdminPanel(false)} />
          </div>
        </div>
      )}

      {showHelp && (
        <div
          className="panel-overlay"
          onClick={(e) => handleOverlayPointer(e, () => setShowHelp(false))}
        >
          <div className="modal-shell help-modal-shell" onClick={(e) => e.stopPropagation()}>
            <HelpPanel onClose={() => setShowHelp(false)} />
          </div>
        </div>
      )}
      
      {showDailyRewards && (
        <div
          className="panel-overlay"
          onClick={(e) => handleOverlayPointer(e, () => setShowDailyRewards(false))}
        >
          <div className="modal-shell">
            <DailyRewardsPanel onClose={() => setShowDailyRewards(false)} />
          </div>
        </div>
      )}

      {showBoostEvents && (
        <div
          className="panel-overlay"
          onClick={(e) => handleOverlayPointer(e, () => setShowBoostEvents(false))}
        >
          <div className="modal-shell">
            <BoostEventsPanel onClose={() => setShowBoostEvents(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

